import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from '../../../util/lang';
import { Button } from '@material-ui/core';
import instance from '../../../axios-default';
import Modal from '../../../components/Modal/modal';
import RestorationForm from './RestorationForm';
import UploadImpressionTab from '../../../components/UploadImpressionTab/UploadImpressionTab';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchemaRestorationForm } from './RestorationFormQuestion';
import { easySnackbar } from '../../../store/Noti/noti.actions';
import { ClinicalApprovementOption } from '../clinicalApprovementOptionConstants';
import PercentageProgressRing from '../../../components/PercentageProgressRing';
import { useHistory } from 'react-router-dom';

//Remark: isOpenExternal and setIsOpenExternal are used to control the modal from the parent component (on top of this component's state)
const RestorativePhaseButton = ({ 
    className = undefined,
    caseId,
    planId = undefined,
    isShowButton = true,
    isOpenExternal = undefined,
    setIsOpenExternal = undefined,
    displayFirstFitFlag = false,
    treatmentTypes = undefined,
}) => {
    const history = useHistory();
    const get = useLang();
    const dispatch = useDispatch();
    const progressBarRef = useRef(null);
    const clinicalApprovementTreatmentPlanData = useSelector(
        (state) => state.smileDesign.clinicalApprovementTreatmentPlanData
    );
    const planIdinMultiplePlan = useSelector((state) => state.smileDesign.planId);

    const methods = useForm({
        resolver: yupResolver(validationSchemaRestorationForm), // Explain: link two schema together
        mode: 'onChange',
    });

    const reloadPageFunc = useCallback(() => {
        history.push('/blank');
        history.replace(`/patients/${caseId}`);
    }, []);

    const [isOpen, setIsOpen] = useState(false);
    const uploadFile = useSelector((state) => state.uploadFile);
    const [isLoading, setIsLoading] = useState(false);

    const isMarieGroup = useMemo(() => {
        let value = JSON.parse(localStorage.getItem('isMarieGroup'));
        return value;
    }, []);
    useEffect(() => {
        methods.trigger();
    }, []);
    if(displayFirstFitFlag){
        /** Navigates to the new full-page workflow. */
        const handleOpen = () => history.push(`/patients/${caseId}/restorative-phase-case-intake`, { treatmentTypes });

        if (!isShowButton) return null;
        return <Button className={className} onClick={handleOpen}>{get('@PatientInfo.case_info.actionsList.restorativePhase')}</Button>;
    }else{
        

        const handleOpen = () => {
            if (setIsOpenExternal === undefined) {
                setIsOpen(true);
            } else {
                setIsOpenExternal(true);
            }
        };

        const handleClose = () => {
            if (setIsOpenExternal === undefined) {
                setIsOpen(false);
            } else {
                setIsOpenExternal(false);
            }
        };

        const onSubmit = async (data) => {
            try {
                let submitData = { ...data }; // Purpose: avoid readonly object
                submitData['isMarieGroup'] = isMarieGroup;
                let currentPlanId = planId === undefined ? planIdinMultiplePlan : planId;
                if (submitData) {
                    // Debug
                    // console.log(JSON.parse((Object.fromEntries(form).body)))
                    setIsLoading(true);

                    if (clinicalApprovementTreatmentPlanData === ClinicalApprovementOption[2] && currentPlanId) {
                        let form = new FormData();
                        form.append(
                            'body',
                            JSON.stringify({
                                restorationForm: submitData,
                                approvementOption:
                                    clinicalApprovementTreatmentPlanData === ''
                                        ? null
                                        : clinicalApprovementTreatmentPlanData,
                            })
                        );

                        const result = await instance.post(
                            `case/restorationForm/approveSmileDesignPlan/${caseId}/${currentPlanId}`,
                            form,
                            {
                                onUploadProgress: progressBarRef.current?.onProgressChange,
                            }
                        );

                        if (result.data.success) {
                            dispatch(
                                easySnackbar(
                                    `${get('@PatientInfo.smileDesign.approveSmileDesignAndRestoration')}`,
                                    'success',
                                    true
                                )
                            );
                            handleClose();
                            reloadPageFunc();
                        }
                    } else {
                        const impressionData = await uploadFile.handleImpressionData(); // Purpose: submit impression data
                        if (impressionData === undefined) {
                            throw new Error('Impression data is undefined');
                        }

                        let form = impressionData.impressionFile;
                        form.append(
                            'body',
                            JSON.stringify({
                                restorationForm: submitData,
                                case_impression: {
                                    ...impressionData.case_impression,
                                    ...impressionData.newImpressionUpload,
                                },
                                approvementOption:
                                    clinicalApprovementTreatmentPlanData === ''
                                        ? null
                                        : clinicalApprovementTreatmentPlanData,
                            })
                        );

                        const result = await instance.post(`case/restorationForm/${caseId}`, form, {
                            onUploadProgress: progressBarRef.current?.onProgressChange,
                        });

                        if (result.data.success) {
                            dispatch(
                                easySnackbar(`${get('@PatientInfo.smileDesign.approveRestoration')}`, 'success', true)
                            );
                            handleClose();
                            reloadPageFunc();
                        }
                    }
                }
            } catch (err) {
                dispatch(easySnackbar(err?.response?.data?.message, 'error'));
            } finally {
                setIsLoading(false);
            }
        };

        

        return (
            <FormProvider {...methods}>
                {isShowButton && (
                    <Button className={className} onClick={handleOpen}>
                        {get('@PatientInfo.case_info.actionsList.restorativePhase')}
                    </Button>
                )}

                <Modal
                    className="prescriptionNotes"
                    modal="prescriptionNotes"
                    title={'Restoration tool form'}
                    handleClose={handleClose}
                    open={isOpenExternal === undefined ? isOpen : isOpenExternal}
                    submitButton={true}
                    modalError={false}
                    handleDataSave={methods.handleSubmit(onSubmit)}
                >
                    <RestorationForm />
                    {clinicalApprovementTreatmentPlanData !== ClinicalApprovementOption[2] && (
                        <UploadImpressionTab caseId={caseId} />
                    )}
                </Modal>

                <PercentageProgressRing ref={progressBarRef} open={isLoading} label={undefined} />
            </FormProvider>
        ); 
    }
};

export default memo(RestorativePhaseButton);
